import { useAuthStore } from '../store/authStore';
import { navigateTo } from '../utils/navigation';

const BASE = '/api';

// Circular buffer of last 20 API errors — consumed by DebugPanel
export interface ApiErrorEntry {
  ts: number;
  method: string;
  path: string;
  status: number;
  message: string;
}
const MAX_ERRORS = 20;
export const apiErrorLog: ApiErrorEntry[] = [];

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Singleton promise: if multiple requests get 401 simultaneously only ONE
// refresh call is sent to the server. All concurrent callers wait for and
// reuse the same result, preventing token-rotation race conditions.
let _refreshPromise: Promise<string | null> | null = null;

export async function refreshToken(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.data?.twoFactorPending) {
        // Refresh gave us another pending token — persist flag so RequireAuth can redirect
        const pending = data.data.accessToken as string | undefined;
        useAuthStore.getState().setTwoFactorPending(true, pending ?? undefined);
        return null; // signal: need 2FA, not a normal token
      }
      const token = data.data?.accessToken;
      if (token) useAuthStore.getState().setToken(token);
      return token;
    } catch {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const store = useAuthStore.getState();
  const token = store.accessToken;

  const headers: Record<string, string> = {
    ...(options.body && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> ?? {}),
  };

  let res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // Try refresh on 401
  if (res.status === 401 && token) {
    const newToken = await refreshToken();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(`${BASE}${path}`, { ...options, headers, credentials: 'include' });
    } else {
      useAuthStore.getState().logout();
      navigateTo('/login', { replace: true });
      throw new ApiError(401, 'Session expired');
    }
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let bodyJson: Record<string, unknown> | null = null;
    try {
      bodyJson = await res.json();
      const rawErr = bodyJson!.error;
      if (typeof rawErr === 'string') message = rawErr;
      else if (rawErr && typeof rawErr === 'object') {
        const fe = (rawErr as Record<string, unknown>).fieldErrors;
        const msgs = fe ? Object.values(fe as Record<string, string[]>).flat() : [];
        message = msgs.length ? msgs.join('; ') : ((bodyJson!.message as string) ?? message);
      } else {
        message = (bodyJson!.message as string) ?? message;
      }
    } catch { /* empty */ }

    // 2FA pending: redirect to verify page, swallow the error
    if (res.status === 403 && bodyJson && bodyJson.twoFactorPending) {
      navigateTo('/auth/2fa-verify');
      // Return a never-resolving promise so callers don't process broken data
      return new Promise<T>(() => {});
    }

    const method = (options.method ?? 'GET').toUpperCase();
    apiErrorLog.push({ ts: Date.now(), method, path, status: res.status, message });
    if (apiErrorLog.length > MAX_ERRORS) apiErrorLog.shift();
    throw new ApiError(res.status, message);
  }

  // Handle empty responses
  const contentType = res.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return res as unknown as T;
  }

  const json = await res.json();
  return (json.data !== undefined ? json.data : json) as T;
}

export function apiGet<T>(path: string) {
  return apiFetch<T>(path);
}

export async function apiGetText(path: string): Promise<string> {
  const store = useAuthStore.getState();
  const token = store.accessToken;

  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let res = await fetch(`${BASE}${path}`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && token) {
    const newToken = await refreshToken();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(`${BASE}${path}`, { method: 'GET', headers, credentials: 'include' });
    } else {
      useAuthStore.getState().logout();
      navigateTo('/login', { replace: true });
      throw new ApiError(401, 'Session expired');
    }
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const bodyJson = await res.json();
      const rawErr2 = bodyJson.error;
      if (typeof rawErr2 === 'string') message = rawErr2;
      else if (rawErr2 && typeof rawErr2 === 'object') {
        const fe2 = (rawErr2 as Record<string, unknown>).fieldErrors;
        const msgs2 = fe2 ? Object.values(fe2 as Record<string, string[]>).flat() : [];
        message = msgs2.length ? msgs2.join('; ') : ((bodyJson.message as string) ?? message);
      } else {
        message = (bodyJson.message as string) ?? message;
      }
    } catch { /* empty */ }
    throw new ApiError(res.status, message);
  }

  return res.text();
}

export function apiPost<T>(path: string, body?: unknown) {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(path: string, body?: unknown) {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(path: string) {
  return apiFetch<T>(path, { method: 'DELETE' });
}

export function apiPut<T>(path: string, body?: unknown) {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiUpload<T>(path: string, formData: FormData) {
  return apiFetch<T>(path, { method: 'POST', body: formData });
}

export async function apiDownloadBlob(path: string, filename: string) {
  const store = useAuthStore.getState();
  let token = store.accessToken;

  const doFetch = (t: string | null) =>
    fetch(`${BASE}${path}`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });

  let res = await doFetch(token);
  if (res.status === 401 && token) {
    const newToken = await refreshToken();
    if (newToken) {
      res = await doFetch(newToken);
    } else {
      useAuthStore.getState().logout();
      navigateTo('/login', { replace: true });
      throw new ApiError(401, 'Session expired');
    }
  }
  if (!res.ok) {
    const entry = { ts: Date.now(), method: 'GET', path, status: res.status, message: `HTTP ${res.status}` };
    apiErrorLog.push(entry);
    if (apiErrorLog.length > MAX_ERRORS) apiErrorLog.shift();
    throw new ApiError(res.status, `HTTP ${res.status}`);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
